import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import heroBackground from '../../images/earthmoon.jpg'
import V2Layout from '../../components/V2Layout'
import SEO from '../../components/SEO'
import axios from 'axios'
import useCompanyStats from '../../components/useCompanyStats'

const API = ''

const PageV2 = () => {
    const { stats } = useCompanyStats()
    const [categories, setCategories] = useState([])
    const [videoSrc, setVideoSrc] = useState(null)
    const [videoError, setVideoError] = useState(false)
    const [scrollY, setScrollY] = useState(0)

    useEffect(() => {
        axios.get(`${API}/api/categories`).then(r => setCategories(r.data)).catch(() => { })
    }, [])

    useEffect(() => {
        // Track scroll position for hardware-accelerated parallax effect
        const handleScroll = () => {
            setScrollY(window.scrollY)
        }
        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        // Fetch videos from the backend and pick the rotated one
        axios.get(`${API}/api/videos`)
            .then(res => {
                if (Array.isArray(res.data) && res.data.length > 0) {
                    rotateVideo(res.data);
                } else {
                    setVideoError(true);
                }
            })
            .catch(err => {
                console.warn('Failed to fetch videos from API:', err);
                setVideoError(true);
            });
    }, [])

    const rotateVideo = (videoList) => {
        try {
            const indexKey = 'pfc_hero_video_index'
            const lastIndex = localStorage.getItem(indexKey)
            let nextIndex = 0
            if (lastIndex !== null) {
                nextIndex = (parseInt(lastIndex, 10) + 1) % videoList.length
            }
            localStorage.setItem(indexKey, nextIndex.toString())
            setVideoSrc(videoList[nextIndex])
        } catch (e) {
            console.error('Failed to rotate video background:', e)
            if (videoList.length > 0) {
                setVideoSrc(videoList[0])
            } else {
                setVideoError(true)
            }
        }
    }

    const handleVideoError = () => {
        console.warn(`Video background failed to load: ${videoSrc}. Falling back to default.`);
        setVideoError(true)
    }

    return (
        <V2Layout>
            <SEO
                title="Aerospace Valves & Pressure Controls"
                description="Custom aerospace valves, regulators & flow controls for launch vehicles, propulsion & defense. 400,000+ valves shipped. Made in Lincoln, CA."
            />
            {/* ── HERO ── */}
            <section
                className="v2-hero"
                style={{ backgroundImage: `url(${heroBackground})` }}
            >
                {!videoError && videoSrc && (
                    <div className="v2-hero-video-container">
                        <video
                            src={videoSrc}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="v2-hero-video"
                            onError={handleVideoError}
                            style={{
                                transform: `translate3d(0, ${scrollY * 0.25}px, 0)`
                            }}
                        />
                    </div>
                )}
                <div className="v2-hero-overlay" />
                <div className="v2-hero-content">
                    <div className="v2-hero-label">Precision Fluid Controls</div>
                    <h1 className="v2-hero-title">
                        Aerospace valves and pressure controls for extreme environments
                    </h1>
                    <p className="v2-hero-sub">
                        Custom engineered dual-use valves, regulators and flow control components for launch vehicles, propulsion systems, ground support, aviation, industrial, defense, maritime and medical devices.  Designed, manufactured and tested in Lincoln, California.
                    </p>
                    <div className="v2-hero-ctas">
                        <Link to="/products" className="v2-btn-primary">Explore Products</Link>
                        <Link to="/contactus" className="v2-btn-outline">Contact Us</Link>
                    </div>
                </div>
            </section>

            {/* ── STATS BAR ── */}
            <section className="v2-stats-bar">
                <div className="v2-stats-inner">
                    <div className="v2-stat">
                        <span className="v2-stat-num">{new Date().getFullYear() - parseInt(stats.year_founded)}+</span>
                        <span className="v2-stat-label">Years Experience</span>
                    </div>
                    <div className="v2-stat">
                        <span className="v2-stat-num">{stats.facility_size}</span>
                        <span className="v2-stat-label">Sq Ft Facility</span>
                    </div>
                    <div className="v2-stat">
                        <span className="v2-stat-num">400,000+</span>
                        <span className="v2-stat-label">Valves Shipped</span>
                    </div>
                    <div className="v2-stat">
                        <span className="v2-stat-num">100%</span>
                        <span className="v2-stat-label">Made in USA</span>
                    </div>
                </div>
            </section>

            {/* ── PRODUCT FAMILIES → /products gallery + category filter ── */}
            <section className="v2-products" id="products">
                <div className="v2-products-inner">
                    <div className="v2-products-header">
                        <div>
                            <p className="v2-section-label">What We Manufacture</p>
                            <h2 className="v2-products-title">Product Categories</h2>
                            <p className="v2-products-lead">
                                Explore representative components, full category lists, and capabilities on the product catalog.
                            </p>
                        </div>
                        <Link to="/products" className="v2-btn-primary" style={{ fontSize: '0.8125rem' }}>
                            Full catalog →
                        </Link>
                    </div>
                    <div className="v2-category-grid">
                        {categories.map((c, i) => (
                            <Link key={c.id} to={`/products?category_id=${c.id}`} className="v2-category-card">
                                <div className="v2-category-card-head">
                                    <span className="v2-category-card-num">
                                        {c.icon ? <i className={`ti ${c.icon}`}></i> : String(i + 1).padStart(2, '0')}
                                    </span>
                                    <h3 className="v2-category-card-title">{c.name}</h3>
                                </div>
                                {c.description && <p className="v2-category-card-desc">{c.description}</p>}
                                <span className="v2-category-card-cta">View products →</span>
                            </Link>
                        ))}
                        <Link to="/products/custom" className="v2-category-card" style={{ gridColumn: '1 / -1' }}>
                            <div className="v2-category-card-head">
                                <span className="v2-category-card-num">
                                    <i className="ti ti-layout-sidebar-left-collapse"></i>
                                </span>
                                <h3 className="v2-category-card-title">Custom Products</h3>
                            </div>
                            <p className="v2-category-card-desc">Custom Products including sleeve valves, Throttling Valves, Vent Valves, Butterfly Valves, Solenoid and more</p>
                            <span className="v2-category-card-cta">View products →</span>
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── AEROSPACE VALVES OVERVIEW (crawlable SEO content) ── */}
            <section className="v2-products" id="aerospace-valves">
                <div className="v2-products-inner">
                    <p className="v2-section-label">Flight-Critical Fluid Control</p>
                    <h2 className="v2-products-title">Aerospace Valves Built for Flight-Critical Systems</h2>
                    <p className="v2-products-lead" style={{ maxWidth: '720px' }}>
                        For over two decades, Precision Fluid Controls has engineered aerospace valves
                        that hold tight tolerances under the pressure, temperature, and vibration extremes
                        of spaceflight — with more than 400,000 valves shipped from our Lincoln, California
                        facility.
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2.5rem', marginTop: '3rem' }}>
                        <div>
                            <h3 className="v2-category-card-title">Valve Types</h3>
                            <p className="v2-products-lead" style={{ marginTop: '0.5rem' }}>
                                Check valves, relief valves, ball valves, pressure regulators, and cryogenic
                                butterfly valves — custom engineered or adapted from proven designs, in line
                                sizes from ¼" to 8" and pressure ratings up to 15,000 PSIG. Cryogenic valves
                                are qualified for liquid oxygen, methane, and hydrogen service.
                            </p>
                        </div>
                        <div>
                            <h3 className="v2-category-card-title">Applications</h3>
                            <p className="v2-products-lead" style={{ marginTop: '0.5rem' }}>
                                PFC aerospace valves fly on launch vehicles and propulsion systems, and operate
                                in ground support equipment at Kennedy Space Center, Stennis, Marshall, Vandenberg,
                                Edwards, and Arnold. The same capability serves aviation, defense, maritime,
                                and medical programs.
                            </p>
                        </div>
                        <div>
                            <h3 className="v2-category-card-title">Certifications &amp; Quality</h3>
                            <p className="v2-products-lead" style={{ marginTop: '0.5rem' }}>
                                Production runs under an AS9100D-certified quality system, with cleaning,
                                passivation, assembly, and environmental testing performed in-house. Every
                                valve is functionally tested and traceable from raw material through final
                                inspection.
                            </p>
                        </div>
                    </div>

                    <p className="v2-products-lead visually-hidden" style={{ marginTop: '2.5rem', maxWidth: '720px' }}>
                        Learn more about our <Link to="/aerospace-valves">aerospace valve types, applications,
                        and certifications</Link>, explore <Link to="/capabilities">manufacturing capabilities</Link>,
                        or review our <Link to="/certifications">quality certifications</Link>.
                    </p>
                </div>
            </section>
        </V2Layout>
    )
}

export default PageV2
